import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ConfirmService } from '../../../../Utils/_services/confirmation-dialog.service';
import { ToastrService } from 'ngx-toastr';
import { EventService } from '../../../../Utils/_services/admin/event/event.service';
import { IEventDetailView } from '../../../../Utils/_models/event.model';
import { SpinnerLoaderComponent } from '../../../../shared/components/spinner-loader/spinner-loader.component';
;



@Component({
  selector: 'app-event-detail',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TagModule,
    SpinnerLoaderComponent
  ],
  templateUrl: './event-detail.component.html',
  styleUrls: ['./event-detail.component.css'],
})
export class EventDetailComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private eventService = inject(EventService);
  private confirmationService = inject(ConfirmService);
  private toastr = inject(ToastrService);
  showAllAttendees = signal(false);
  readonly ATTENDEES_LIMIT = 3;

  event = signal<IEventDetailView | null>(null);
  isLoading = signal(false);
  error = signal<string | null>(null);

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadEvent(id);
  }

  loadEvent(id: string): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.eventService.getEventById(id).subscribe({
      next: (res) => {
        const d = res.data;
        const view: IEventDetailView = {
          ...d,
          image : d.image,
          timeRange: this.formatTimeRange(d.startTime, d.endTime),
          attendees: d.attendees.map((a) => ({
            ...a,
            initials: this.getInitials(a.name),
            avatarBg: this.getAvatarBg(a.name),
          })),
        };
        this.event.set(view);
        this.isLoading.set(false);
      },
      error: () => {
        this.error.set('Failed to load event details.');
        this.isLoading.set(false);
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/admin/events']);
  }

  async deleteEvent() {
    const ev = this.event();
    if (!ev) return;

    const confirmed = await this.confirmationService.confirm({
      header: 'Delete Event',
      bodyHeader: 'Are you sure?',
      bodySubtitle: `You are about to delete "${ev.title}". This cannot be undone.`,
      yesButtonText: 'Delete Event',
      yesBtnCss: 'btn-danger',
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });

    if (confirmed) {
      this.eventService.deleteEvent(ev._id).subscribe({
        next: () => {
          this.toastr.success(`"${ev.title}" deleted successfully.`, 'Deleted');
          setTimeout(() => this.router.navigate(['/admin/events']), 1500);
        },
        error: () => {
          this.toastr.error('Failed to delete event.', 'Error');
        },
      });
    }
  }

  private formatTimeRange(start: string, end: string): string {
    const fmt = (d: string) =>
      new Date(d).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    return `${fmt(start)} – ${fmt(end)}`;
  }

  private getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0] ?? '')
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  private getAvatarBg(name: string): string {
    const colors = ['#fee2e2', '#ede9fe', '#dcfce7', '#fef9c3', '#dbeafe', '#fce7f3'];
    const index = name
      .split('')
      .reduce((acc, c) => acc + c.charCodeAt(0), 0) % colors.length;
    return colors[index];
  }
  visibleAttendees = computed(() => {
    const ev = this.event();
    if (!ev) return [];
    return this.showAllAttendees()
      ? ev.attendees
      : ev.attendees.slice(0, this.ATTENDEES_LIMIT);
  });

  toggleAttendees(): void {
    this.showAllAttendees.update(v => !v);
  }
}